import { usePublishedData } from "../lib/published-data";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import bibleStyles from "../styles/bible.css?url";
import {
  bibleSectionsForRole,
  readBibleSynergyRole,
} from "../../../shared/src/league-bible-runtime";
import { bibleRolesInOrder } from "../../src/lib/bible-handbook";
import { ChapterPage } from "../components/bible/chapter-page";
import { Handbook } from "../components/bible/handbook";
import { bibleHead } from "../components/bible/head";
import { DocumentContent } from "../components/document-content";
import { documentHead, useShell } from "../lib/document-route";
import { creatorPageHead, shellFrom, withStylesheet } from "../lib/head";
import { loadBibleChapter } from "../server/bible";

type Search = { role?: string; preview?: string };

/**
 * A League Bible chapter (/bible/:chapter): its body for a visitor who may read it, its gate for
 * anyone else. Choosing a Synergies role narrows the chapter without asking the server again.
 * With the Bible off, the address is a page of the creator's own.
 */
export const Route = createFileRoute("/bible/$chapter")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    role: typeof search.role === "string" && search.role ? search.role : undefined,
    preview: search.preview === "1" ? "1" : undefined,
  }),
  loader: async ({ params }) => {
    const page = await loadBibleChapter({ data: params.chapter });
    if (!page) throw notFound();
    if (page.kind === "redirect") throw redirect({ href: page.to, statusCode: 302 });
    return page;
  },
  // A role is another view of the same chapter; opening it follows the usual freshness rules.
  shouldReload: ({ cause }) => (cause === "stay" ? false : undefined),
  head: ({ matches, loaderData, match }) => {
    if (loaderData?.kind === "page")
      return withStylesheet(
        documentHead(matches, loaderData, match.pathname, {
          title: (shell) => `Page not found | ${shell.creator.name}`,
          description: () => "This page could not be found.",
        }),
        bibleStyles,
      );
    const shell = shellFrom(matches);
    if (!shell || !loaderData) return {};
    const head = creatorPageHead(shell, {
      pathname: loaderData.head.canonicalPath,
      title: loaderData.head.title,
      description: loaderData.head.description,
      canonicalPath: loaderData.head.canonicalPath,
      article: true,
    });
    return bibleHead(withStylesheet(head, bibleStyles), shell);
  },
  component: Chapter,
});

function Chapter() {
  const shell = useShell();
  const path = `/bible/${Route.useParams().chapter}`;
  const { data } = usePublishedData(Route.useLoaderData(), shell, path, "page");
  return data.kind === "page" ? <OwnPage data={data} /> : <NativeChapter page={data} />;
}

function OwnPage({
  data,
}: {
  data: Extract<ReturnType<typeof Route.useLoaderData>, { kind: "page" }>;
}) {
  const shell = useShell();
  return <DocumentContent data={data} shell={shell} />;
}

function NativeChapter({
  page,
}: {
  page: Extract<ReturnType<typeof Route.useLoaderData>, { kind: "chapter" }>;
}) {
  const { role } = Route.useSearch();
  const { chapter } = page;
  const handbook = page.design === "handbook";
  const roles = bibleRolesInOrder(chapter.sections);
  const synergyRole =
    chapter.key === "synergies"
      ? handbook && !role && roles.length
        ? roles[0]
        : readBibleSynergyRole(role ?? null, chapter.sections)
      : undefined;
  const visibleSections = synergyRole
    ? bibleSectionsForRole(chapter.sections, synergyRole)
    : chapter.sections;
  return handbook ? (
    <Handbook page={page} sections={visibleSections} synergyRole={synergyRole} />
  ) : (
    <ChapterPage page={page} synergyRole={synergyRole} visibleSections={visibleSections} />
  );
}
